import React, { useState } from "react";

import Panel from "ui/Panel";

import {
  GetIndexAdvisorConfigForDatabaseQuery,
  useGetIndexAdvisorConfigForDatabaseQuery,
} from "./gql/Query.tables.generated";

import Loading from "ui/Loading";
import Grid from "ui/Grid";
import Button from "ui/Button";
import { SpinnerIcon } from "ui/Icons";
import {
  GetCheckConfigQuery,
  useGetCheckConfigQuery,
} from "./gql/Query.generated";

import { CHECK_OPTIONS } from "features/Checks/CheckConfig";
import { useRoutes } from "utils/routes";
import { Link } from "react-router-dom";
import { useCurrentServer } from "providers/WithCurrentOrganization";
import Details from "./Details";

type GetCheckConfigType = GetCheckConfigQuery["getCheckConfig"];
type IndexAdvisorConfig =
  GetIndexAdvisorConfigForDatabaseQuery["getIndexAdvisorConfigForDatabase"];

const checkGroupAndName = "index_advisor/indexing_engine";

const Config: React.FunctionComponent<{
  databaseId: string;
}> = ({ databaseId }) => {
  const { data, loading, error } = useGetCheckConfigQuery({
    variables: {
      databaseId,
      checkGroupAndName: checkGroupAndName,
    },
  });
  return (
    <Configurations
      loading={loading}
      error={error}
      databaseId={databaseId}
      checkData={data?.getCheckConfig}
    />
  );
};

type ConfigData = {
  name: string;
  help: string;
};

const ignoredKey = "ignored_table_size_threshold_mb";
const writeOptimizedKey = "write_heavy_threshold";
const readOptimizedKey = "read_heavy_threshold";

const Configurations: React.FunctionComponent<{
  loading: boolean;
  error: Error;
  databaseId: string;
  checkData: GetCheckConfigType;
}> = ({ loading, error, databaseId, checkData }) => {
  const { serverCheckConfigure } = useRoutes();
  const server = useCurrentServer(true);
  const [focus, setFocus] = useState<string>();
  function handleCloseDetails() {
    setFocus(null);
  }

  const {
    data: tData,
    loading: tLoading,
    error: tError,
  } = useGetIndexAdvisorConfigForDatabaseQuery({
    variables: {
      databaseId,
    },
  });

  if (loading || error) {
    return (
      <Panel title="Configurations">
        <Loading error={!!error} />
      </Panel>
    );
  }
  const checkSettings = checkData.settings;
  const [checkGroup, checkName] = checkGroupAndName.split("/", 2);
  const checkDescription = CHECK_OPTIONS[checkGroup][checkName];
  const ignored = checkDescription.find((v) => v.key === ignoredKey);
  const writeOptimized = checkDescription.find(
    (v) => v.key === writeOptimizedKey,
  );
  const readOptimized = checkDescription.find(
    (v) => v.key === readOptimizedKey,
  );
  function addThresholdValueToHelp(help: string, threshold: string) {
    return help.replace("this threshold", `the threshold (${threshold})`);
  }

  const gridData: ConfigData[] = [
    {
      name: "Custom",
      help: "Tables using a custom configuration",
    },
    {
      name: "Ignored",
      help: addThresholdValueToHelp(
        ignored.help,
        `${checkSettings[ignoredKey]} MB`,
      ),
    },
    {
      name: "Write-optimized",
      help: addThresholdValueToHelp(
        writeOptimized.help,
        checkSettings[writeOptimizedKey],
      ),
    },
    {
      name: "Read-optimized",
      help: addThresholdValueToHelp(
        readOptimized.help,
        checkSettings[readOptimizedKey],
      ),
    },
    {
      name: "Balanced",
      help: "All tables that do not match to above conditions will use the Balanced configuration",
    },
  ];

  return (
    <>
      {focus != null && (
        <Details
          databaseId={databaseId}
          data={tData.getIndexAdvisorConfigForDatabase}
          settingName={focus}
          onClose={handleCloseDetails}
        />
      )}
      <Panel
        title="Configurations"
        secondaryTitle={
          <Link to={serverCheckConfigure(server.humanId, checkGroupAndName)}>
            Configure thresholds
          </Link>
        }
      >
        <Grid
          data={gridData}
          columns={[
            {
              field: "name",
              header: "Configuration",
              width: "1fr",
              renderer: ConfigDetailsCell,
              disableSort: true,
            },
            {
              field: "name",
              header: "Applied To",
              width: "200px",
              renderer: function AppliedCell({ fieldData }) {
                return (
                  <AppliedDetailCell
                    loading={tLoading}
                    error={tError}
                    data={tData?.getIndexAdvisorConfigForDatabase}
                    settingsName={fieldData}
                    setFocus={setFocus}
                  />
                );
              },
              disableSort: true,
            },
          ]}
        />
      </Panel>
    </>
  );
};

const ConfigDetailsCell: React.FunctionComponent<{
  rowData: ConfigData;
}> = ({ rowData }) => {
  return (
    <div>
      <div className="text-[16px] text-[#606060] my-1">{rowData.name}</div>
      <div className="whitespace-normal">{rowData.help}</div>
    </div>
  );
};

const AppliedDetailCell: React.FunctionComponent<{
  loading: boolean;
  error: Error;
  data: IndexAdvisorConfig;
  settingsName: string;
  setFocus: React.Dispatch<React.SetStateAction<string>>;
}> = ({ loading, error, data, settingsName, setFocus }) => {
  if (loading) {
    return <SpinnerIcon />;
  }
  if (error) {
    return <>unable to fetch data</>;
  }
  if (!data) {
    return <>no data</>;
  }
  const tableCount = data.filter(
    (val) => val.settingsName === settingsName,
  ).length;

  return (
    <Button bare onClick={() => setFocus(settingsName)} variant="link">
      {tableCount} Tables
    </Button>
  );
};

export default Config;
